import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import {
  Bold,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Loader2,
  Quote,
  Redo2,
  Strikethrough,
  Underline,
  Undo2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { IMAGE_ACCEPT, mediaUrl, uploadBlogImage } from "./api";

function ToolButton({ active, disabled, onClick, title, children }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground disabled:opacity-30",
        active && "bg-ginox-green/10 text-ginox-green",
      )}
    >
      {children}
    </button>
  );
}

// HTML in, HTML out. Images upload first and go in with the full API URL.
export default function RichTextEditor({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false } }), Image],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.isEmpty ? "" : editor.getHTML()),
    // Toolbar active states read the editor on every render.
    shouldRerenderOnTransaction: true,
  });

  if (!editor) return null;

  const chain = () => editor.chain().focus();

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const href = window.prompt("Link URL (leave empty to remove)", prev);
    if (href === null) return;
    if (!href.trim()) chain().extendMarkRange("link").unsetLink().run();
    else chain().extendMarkRange("link").setLink({ href: href.trim() }).run();
  };

  const insertImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadBlogImage(file);
      chain().setImage({ src: mediaUrl(path), alt: file.name }).run();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Image upload failed");
    }
    setUploading(false);
  };

  return (
    <div className="rounded-md border border-border bg-white/[0.02]">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border/70 p-1.5">
        <ToolButton title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => chain().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => chain().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Bold" active={editor.isActive("bold")} onClick={() => chain().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Italic" active={editor.isActive("italic")} onClick={() => chain().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Underline" active={editor.isActive("underline")} onClick={() => chain().toggleUnderline().run()}>
          <Underline className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Strikethrough" active={editor.isActive("strike")} onClick={() => chain().toggleStrike().run()}>
          <Strikethrough className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Bullet list" active={editor.isActive("bulletList")} onClick={() => chain().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Numbered list" active={editor.isActive("orderedList")} onClick={() => chain().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Quote" active={editor.isActive("blockquote")} onClick={() => chain().toggleBlockquote().run()}>
          <Quote className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Link" active={editor.isActive("link")} onClick={setLink}>
          <Link2 className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Insert image" disabled={uploading} onClick={() => fileRef.current?.click()}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
        </ToolButton>
        <input ref={fileRef} type="file" accept={IMAGE_ACCEPT} className="hidden" onChange={insertImage} />
        <span className="mx-1 h-5 w-px bg-border" />
        <ToolButton title="Undo" disabled={!editor.can().undo()} onClick={() => chain().undo().run()}>
          <Undo2 className="h-4 w-4" />
        </ToolButton>
        <ToolButton title="Redo" disabled={!editor.can().redo()} onClick={() => chain().redo().run()}>
          <Redo2 className="h-4 w-4" />
        </ToolButton>
      </div>
      <EditorContent editor={editor} className="blog-editor" />
    </div>
  );
}
